import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '../context/themeContext'
import { themes } from '../context/themeContext'
import { propTypes } from 'react-bootstrap/esm/Image'

const useStyles = makeStyles((theme) => ({
  select: {
    color: 'var(--primary-grey)',
    '&:focus': {
      backgroundColor: 'transparent'
    }
  },
  notchedOutline: {
    borderColor: 'var(--primary-grey)'
  },
  inputLabel: {
    color: 'var(--primary-grey)',
    borderColor: 'var(--primary-color)',
    background: 'var(--primary-container)',
    '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(13px, -2px) scale(0.75)'
    },
    fontSize: '18px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px'
    }
  },
  icon: {
    color: 'var(--primary-grey)'
  },
  checkbox: {
    color: 'var(--primary-grey)',
    '&.Mui-checked': {
      color: 'rgb(25, 118, 210)'
    }
  },
  menuItem: {
    '&.Mui-selected': {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
      '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.12)'
      }
    }
  },
  menuItemText: {
    color: 'var(--primary-grey)',
    whiteSpace: 'normal',
  }
}))

// Reusable Filter Select Component
const ReusableFilterSelect = ({
  label,
  options,
  selectedValues = [],
  onChange,
  resetTrigger = false,
  onResetComplete = () => {},
  simpleValues = true,
  valueKey = 'key',
  labelKey = 'label',
  customWidth
}) => {
  const classes = useStyles()
  const { theme } = useTheme()
  const currentTheme = themes[theme]

  // Reset values when resetTrigger changes to true
  useEffect(() => {
    if (resetTrigger) {
      onChange([])
      onResetComplete()
    }
  }, [resetTrigger, onChange, onResetComplete])

  // Handle selection change
  const handleChange = (event) => {
    const value = event.target.value
    onChange(typeof value === 'string' ? value.split(',') : value)
  }

  // Menu props for dropdown styling
  const ITEM_HEIGHT = 48
  const ITEM_PADDING_TOP = 8
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
        marginTop: 0,
        backgroundColor: currentTheme['--primary-container'],
        color: currentTheme['--primary-grey']
      }
    },
    getContentAnchorEl: null,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left'
    }
  }

  // Render selected values in the select box
  const renderValue = (selected) => {
    if (!selected || selected.length === 0) {
      return <></>
    }

    const maxLength = 30
    let result = ''

    if (simpleValues) {
      result = selected.join(', ')
    } else {
      result = selected
        .map((value) => {
          const option = options.find((opt) => opt[valueKey] === value)
          return option ? option[labelKey] : value
        })
        .join(', ')
    }

    if (result.length > maxLength) {
      return result.substring(0, maxLength) + '...'
    }

    return result
  }

  return (
    <FormControl
      variant="outlined"
      style={{ margin: 0, width: customWidth , position: 'relative' }}
      className={classes.containerInput}
    >
      <InputLabel
        id={`multiple-${label.toLowerCase()}-label`}
        style={{
          position: 'absolute',
          top: -4,
          left: 0,
          padding: '0 6px',
          zIndex: 1,
          pointerEvents: 'none'
        }}
        className={classes.inputLabel}
      >
        {label}
      </InputLabel>
      <Select
        labelId={`multiple-${label.toLowerCase()}-label`}
        id={`multiple-${label.toLowerCase()}-checkbox`}
        multiple
        value={selectedValues}
        onChange={handleChange}
        input={
          <OutlinedInput
            label={label}
            classes={{
              notchedOutline: classes.notchedOutline
            }}
          />
        }
        renderValue={renderValue}
        MenuProps={MenuProps}
        style={{ height: 46, fontSize: 16 }}
        displayEmpty
        classes={{
          select: classes.select,
          icon: classes.icon
        }}
      >
        {options.map((option) => {
          const value = simpleValues ? option : option[valueKey]
          const display = simpleValues ? option : option[labelKey]

          return (
            <MenuItem key={value} value={value} className={classes.menuItem}>
              <Checkbox
                checked={selectedValues.indexOf(value) > -1}
                className={classes.checkbox}
              />
              <ListItemText
                primary={display}
                className={classes.menuItemText}
              />
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}

ReusableFilterSelect.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  selectedValues: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  resetTrigger: PropTypes.bool,
  onResetComplete: PropTypes.func,
  simpleValues: PropTypes.bool,
  valueKey: PropTypes.string,
  labelKey: PropTypes.string,
  customWidth:propTypes.number
}
ReusableFilterSelect.defaultProps = {
  customWidth: 300 
}

export default ReusableFilterSelect
